import React, { useState } from 'react';
import { useRouter } from '@hooks/useRouter';
import styled from '@emotion/styled';
import useIsRestricted from '@hooks/useIsRestricted';
import { usePleaseLogIn } from '@hooks/usePleaseLogIn';
import { useTranslation } from 'react-i18next';
import {
  HomeFilled,
  CalendarFilled,
  QuestionCircleFilled,
  CarryOutFilled,
  ContainerFilled,
  MenuOutlined
} from 'antd/icons';
import { Button, Dropdown } from 'antd';
import { useExtremeZoom } from '@hooks/useZoom';
import { useQuery } from '@tanstack/react-query';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import useApi from 'vl-common/src/hooks/useApi';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import { useViewportSize } from 'vl-common/src/hooks/useWindowSize';
import ExercisePlan from '@components/Patients/ExercisePlan/ExercisePlan';
import useAuth from '@vl-core/useAuth';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';

type PageType = 'patients' | 'clinician';

interface NavigationHeaderProps {
  pageType?: PageType;
}
const StyledHeader = styled.div<{ pageType: PageType }>`
  z-index: 1000;
  background-color: var(--productPrimary);
  box-shadow: 0 -6px 6px rgba(0, 0, 0, 0.12);
  padding: 0 auto;
  width: 100%;
  margin: auto;
  height: 48px;

  .button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin: auto;
    max-width: 1482px;
    height: 100%;
    padding: 0 15px;

    @media (max-width: 768px) {
      padding: 0 30px;
    }
  }
  @media (max-width: 1482px) {
    margin: 0;
    padding: 0;
  }
`;

const StyledButton = styled.button<{ active: boolean; badge: string | null; pageType: PageType }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  border: 0;
  cursor: pointer;
  font-size: 14px;
  border-top: 4px solid var(--productPrimary);
  white-space: nowrap;
  padding: 0 20px 4px;

  @media (max-width: 1024px) {
    font-size: 12px;
    padding: 0 10px 4px;
  }

  color: #000000;

  .nav-icon {
    font-size: 18px;
    margin-right: 10px;
    position: relative;
    color: ${({ active }) => (active ? 'var(--active-nav-item-color)' : 'var(--primary-btn-font-color)')};

    @media (max-width: 1024px) {
      font-size: 14px;
      margin-right: 6px;
    }
  }

  .nav-collapsed,
  .nav-text {
    font-size: 18px;
    color: ${({ active }) => (active ? 'var(--active-nav-item-color)' : 'var(--primary-btn-font-color)')};

    @media (max-width: 1024px) {
      font-size: 16px;
    }
  }

  ${({ badge }) =>
    badge !== null
      ? `.nav-icon::after {
        content: '${badge}';
        position: absolute;
        top: -10px;
        left: 10px;
        border-radius: 16px;
        background-color: red;
        height: 15px;
        font-size: 10px;
        padding: 0 6px;
        line-height: 16px;
        color: #ffffff;
        font-weight: bold;
        white-space: nowrap;
      }`
      : ''}
`;

const ExtraActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const ContactButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 4px 12px !important;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  background: #fff !important;
  color: #555 !important;
`;

const MenuButton = ({
  name,
  Icon,
  action,
  testid,
  active,
  badge = null,
  restricted = false,
  pageType
}: NavigationItem & { pageType: PageType }) => {
  const isRestricted = useIsRestricted();
  const [, setPleaseLogIn] = usePleaseLogIn();

  const onClickHandler = restricted && isRestricted ? () => setPleaseLogIn(true) : action;

  return (
    <StyledButton data-testid={testid} onClick={onClickHandler} active={active} badge={badge} pageType={pageType}>
      <Icon aria-hidden className="nav-icon" />
      <span className="nav-collapsed">{name}</span>
    </StyledButton>
  );
};

interface NavigationItem {
  name: string;
  action: () => void;
  active: boolean;
  testid: string;
  Icon: React.ComponentType<any>;
  badge?: string | null;
  restricted?: boolean;
}

export const NavigationHeader = ({ pageType = 'patients' }: NavigationHeaderProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { Api } = useApi();
  const { user } = useAuth();
  const { CONTACT_US_URL } = useRuntimeConfig();

  const { pathname } = router;
  const { t } = useTranslation();
  const { width: screenWidth } = useViewportSize();
  const [isHamburgerMenuExpanded, setHamburgerMenuExpanded] = useState(false);

  const isExtremeZoom = useExtremeZoom();
  const isRestricted = useIsRestricted();
  const [, setPleaseLogIn] = usePleaseLogIn();

  // GUARDED QUERIES: Don't execute patient data tracking on clinician sessions
  const isPatient = pageType === 'patients';

  const { data: allCases = [] } = useQuery({
    queryKey: ['fetchAllCases'],
    queryFn: () =>
      Api.cases.getCases({
        order: 'desc',
        rows: 999,
        sort: 'todo'
      }),
    enabled: isPatient,
    select: ({ status, cases }) => (status === 204 ? [] : cases || [])
  });
  const noCases = allCases.length === 0;

  const { data: todo = [] } = useQuery({
    queryKey: ['getNextSteps'],
    queryFn: () => dispatch(actions.getIncompletedToDosList()),
    enabled: isPatient,
    select: (data) => data.todo
  });

  const physitrackLink = user?.physitrack_link;
  const showExercisePlanButton = isPatient && !isRestricted && !!physitrackLink;

  const todoCount = todo.length;
  const noTodos = todo.length === 0;
  const todoBadgeText = todoCount >= 10 ? '9+' : todoCount.toString();
  // If at least one case & at least one to-do, then show badge on todos page
  const todoBadgeContent = noCases || noTodos ? null : todoBadgeText;
  // If no cases but there are todos, then show badge on home page
  //      They will be patient level, so inaccessible on todos page
  const homeBadgeContent = !noTodos && noCases ? todoBadgeText : null;

  // DYNAMIC MENU BUILDER
  const menuItems: NavigationItem[] = isPatient
    ? [
        {
          name: 'Home',
          action: () => router.push('/patients'),
          active: pathname === '/patients',
          testid: 'patients-dashboard-link',
          Icon: HomeFilled,
          badge: homeBadgeContent
        },
        {
          name: t('Next steps'),
          action: () => router.push('/patients/todos'),
          active: pathname === '/patients/todos',
          restricted: true,
          testid: 'patients-todos-link',
          Icon: CarryOutFilled,
          badge: todoBadgeContent
        },
        {
          name: 'Appointments',
          action: () => router.push('/patients/appointments'),
          active:
            pathname === '/patients/appointments' ||
            pathname.includes('/patients/timeline') ||
            pathname.includes('/patients/session') ||
            pathname.includes('/patients/choose'),
          restricted: true,
          testid: 'patients-appointments-link',
          Icon: CalendarFilled
        },
        {
          name: 'Documents',
          action: () => router.push('/patients/documents'),
          active: pathname === '/patients/documents',
          testid: 'patients-documents-link',
          restricted: true,
          Icon: ContainerFilled
        },
        {
          name: 'Support',
          action: () => router.push('/patients/support'),
          active: pathname === '/patients/support',
          testid: 'patients-support-link',
          Icon: QuestionCircleFilled
        }
      ]
    : [
        {
          name: 'Dashboard',
          action: () => router.push('/clinician'),
          active: pathname === '/clinician',
          testid: 'clinician-dashboard-link',
          Icon: HomeFilled
        },
        {
          name: 'Appointments',
          action: () => router.push('/clinician/appointments'),
          active: pathname.includes('/clinician/appointments'),
          testid: 'clinician-appointments-link',
          Icon: CarryOutFilled
        },
        {
          name: 'Cases',
          action: () => router.push('/clinician/cases'),
          active: pathname.includes('/clinician/cases'),
          testid: 'clinician-cases-link',
          Icon: CalendarFilled
        },
        {
          name: 'Patients',
          action: () => router.push('/clinician/patients'),
          active: pathname.includes('/clinician/patients'),
          testid: 'clinician-patients-link',
          Icon: ContainerFilled
        },
        {
          name: 'Reports',
          action: () => router.push('/clinician/reports'),
          active: pathname.includes('/clinician/reports'),
          testid: 'clinician-reports-link',
          Icon: QuestionCircleFilled
        },
        {
          name: 'Support',
          action: () => router.push('/clinician/support'),
          active: pathname.includes('/clinician/support'),
          testid: 'clinician-support-link',
          Icon: QuestionCircleFilled
        }
      ];

  if (isExtremeZoom) {
    return (
      <Dropdown
        aria-label={t('Navigation menu')}
        menu={{
          items: menuItems.map((item) => ({
            key: item.name,
            label: item.name,
            icon: <item.Icon />,
            onClick: item.restricted && isRestricted ? () => setPleaseLogIn(true) : item.action,
            'data-testid': item.testid
          }))
        }}
        arrow
        trigger={['click']}
        placement="top"
        onOpenChange={setHamburgerMenuExpanded}
      >
        <UnstyledButton
          aria-expanded={isHamburgerMenuExpanded}
          aria-label={t('hamburger')}
          onClick={(e) => e.preventDefault()}
          data-testid="hamburger-menu"
        >
          <MenuOutlined />
        </UnstyledButton>
      </Dropdown>
    );
  }

  return (
    <>
      {screenWidth > 768 && (
        <StyledHeader pageType={pageType}>
          <div className="button-container">
            {menuItems.map((item) => (
              <MenuButton key={item.name} {...item} data-testid={item.testid} pageType={pageType} />
            ))}
            {isPatient && (
              <ExtraActions>
                {showExercisePlanButton && <ExercisePlan url={physitrackLink} />}
                <ContactButton
                  type="link"
                  data-testid="contact-us-button"
                  onClick={() => window.open(CONTACT_US_URL, '_blank')}
                  target="_blank"
                >
                  {t('Contact Us')}
                </ContactButton>
              </ExtraActions>
            )}
          </div>
        </StyledHeader>
      )}
    </>
  );
};
